'use client';
import { createRef, useState, useEffect } from 'react';
import cn from 'classnames';
import { SlidesRef } from 'antd-mobile/es/components/image-viewer/slides';
import ImageViewer from 'antd-mobile/es/components/image-viewer';
import AntdImage from 'antd-mobile/es/components/image';
import Swiper from 'antd-mobile/es/components/swiper';
import { CloseCircleOutline } from 'antd-mobile-icons';
import { PagePhotoAlbumAltTextFragment, MediaFragment } from '@graphqlTypes/sitecore';
import { withFullPath } from '@src/utils/util';
import styles from './index.module.scss';
import { jsBridge } from '@src/utils/js-bridge';

type Props = {
    content: Omit<PagePhotoAlbumAltTextFragment, 'albumList'> & { albumList: MediaFragment[] };
};

const PhotoAlbum = (props: Props) => {
    const [visible, setVisible] = useState(false);
    const [windowSize, setWindowSize] = useState<number>();
    const [currentIndex, setCurrentIndex] = useState<number>();
    const [altList, setAltList] = useState<{ hasError: boolean }[]>([]);
    const PhotoWrapRef = createRef<HTMLDivElement>();
    const ImagePreviewRef = createRef<SlidesRef>();
    const { albumDisplayLayout, albumList } = props.content;

    useEffect(() => {
        if (
            typeof window !== undefined &&
            albumList.length > 0 &&
            PhotoWrapRef?.current &&
            visible
        ) {
            setTimeout(() => {
                const imgList: NodeListOf<HTMLImageElement> | undefined =
                    PhotoWrapRef?.current?.querySelectorAll('.adm-image-viewer-image-wrapper img');

                imgList?.forEach((item, index) => {
                    const altText =
                        (albumList[index] && albumList[index].mediaAltText?.value) || '';
                    if (altText && altText !== item.alt) {
                        item.alt = altText;
                    }
                });
            }, 500);
        }
    }, [PhotoWrapRef, albumList, visible]);

    const onHandleResize = () => {
        setTimeout(() => {
            setWindowSize(window.innerWidth);
        }, 200);
    };

    const insertImgDescription = () => {
        setTimeout(() => {
            const previewImages = document.querySelectorAll('.adm-image-viewer-image-wrapper');
            if (previewImages.length > 0) {
                previewImages.forEach((item, index) => {
                    const descriptionElement = document.createElement('div');
                    descriptionElement.className = styles.altTextPreviewDescription;
                    descriptionElement.innerHTML = albumList[index]?.mediaAltText?.value || '';
                    item.append(descriptionElement);
                });
            }
        }, 500);
    };

    useEffect(() => {
        if (typeof window !== undefined) {
            window.addEventListener('resize', onHandleResize);
        }
        return () => {
            window.removeEventListener('resize', onHandleResize);
        };
    }, []);

    useEffect(() => {
        if (visible) {
            insertImgDescription();
            jsBridge.onBackBtnClicked = () => {
                setVisible(false);
                return true;
            };
        } else {
            jsBridge.onBackBtnClicked = undefined;
        }
        return () => {
            jsBridge.onBackBtnClicked = undefined;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    useEffect(() => {
        const initAltList = Array(albumList?.length || 0).fill('');
        setAltList(initAltList);
        albumList?.forEach((item, index) => {
            const img = new Image();
            img.src = withFullPath(item.mediaSrc?.src || '');
            img.onerror = () => {
                initAltList[index] = { hasError: true };
                setAltList(initAltList);
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [albumList]);

    useEffect(() => {
        if (visible) {
            setVisible(false);
            ImagePreviewRef?.current?.swipeTo(currentIndex || 0, true);
            setVisible(true);
        }
    }, [windowSize, visible, currentIndex, ImagePreviewRef]);

    const SwiperItems = albumList?.map((item, index) => {
        return (
            <Swiper.Item key={index} className='w-full h-full'>
                <div
                    className='relative'
                    onClick={() => {
                        ImagePreviewRef?.current?.swipeTo(index);
                        setCurrentIndex(index);
                        setVisible(true);
                    }}
                >
                    <AntdImage
                        lazy
                        fallback={<>{item.mediaAltText?.value || ''}</>}
                        src={withFullPath(item.mediaSrc?.src)}
                        alt={item.mediaAltText?.value || ''}
                    />
                    {!altList[index]?.hasError && (
                        <div
                            className={cn(
                                'absolute w-full flex justify-between bottom-0 bg-black p-[10px] gap-5 items-center',
                                styles.slidImgDes
                            )}
                        >
                            <div className={cn('text-white', styles.slideImgDesText)}>
                                {item.mediaAltText?.value}
                            </div>
                            <div
                                className={cn(
                                    'bg-[#CCC] text-black py-[1px] px-[10px] rounded-2xl',
                                    styles.slideImgTotal
                                )}
                            >{`${index + 1} / ${albumList.length}`}</div>
                        </div>
                    )}
                </div>
            </Swiper.Item>
        );
    });

    const previewImages = albumList?.map((item) => withFullPath(item.mediaSrc?.src)) || [];

    const ImagePreview = (
        <ImageViewer.Multi
            ref={ImagePreviewRef}
            images={previewImages}
            visible={visible}
            onClose={() => setVisible(false)}
            classNames={{
                mask: styles.altTextPreviewWrap,
                body: styles.altTextPreviewImg,
            }}
            onIndexChange={setCurrentIndex}
            renderFooter={() => {
                return (
                    <>
                        <div className='fixed right-5 top-8' onClick={() => setVisible(false)}>
                            <CloseCircleOutline width={30} height={30} color='#FFFFFF' />
                        </div>
                    </>
                );
            }}
        />
    );

    const Slider = (
        <>
            <Swiper>{SwiperItems}</Swiper>
            {ImagePreview}
        </>
    );

    const Gallery = (
        <>
            <div className='grid grid-cols-2 gap-4'>
                {albumList?.map((item, index) => {
                    return (
                        <div
                            key={index}
                            onClick={() => {
                                ImagePreviewRef?.current?.swipeTo(index);
                                setCurrentIndex(index);
                                setVisible(true);
                            }}
                        >
                            <AntdImage
                                lazy
                                fallback={<>{item.mediaAltText?.value || ''}</>}
                                src={withFullPath(item.mediaSrc?.src)}
                                alt={item.mediaAltText?.value || ''}
                            />
                        </div>
                    );
                })}
            </div>
            {ImagePreview}
        </>
    );

    return (
        <div ref={PhotoWrapRef} className={styles.photoAlbumAltText}>
            {albumDisplayLayout?.value === 'Slider' ? Slider : Gallery}
        </div>
    );
};

export default PhotoAlbum;
